import React, { useContext, useState, useEffect } from "react";
import { auth } from "../firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { getUserByID } from "../Components/utils";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

const CACHED_USER_DETAILS_KEY = "bbr.cachedUserDetails";

function readCachedUserDetails() {
  try {
    const raw = localStorage.getItem(CACHED_USER_DETAILS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeCachedUserDetails(details) {
  try {
    if (details) {
      localStorage.setItem(CACHED_USER_DETAILS_KEY, JSON.stringify(details));
    } else {
      localStorage.removeItem(CACHED_USER_DETAILS_KEY);
    }
  } catch (_) {
    /* ignore quota / private mode errors */
  }
}

export default function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);
  // Hydrate from localStorage so the UI (sidebar, header) can render
  // immediately on slow connections instead of waiting for Firebase.
  const cachedDetails = readCachedUserDetails();
  const [userDetails, setUserDetails] = useState(cachedDetails);
  const [userRole, setUserRole] = useState(cachedDetails?.role || null);
  const [roleLoading, setRoleLoading] = useState(!cachedDetails);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [token, setToken] = useState(null);

  function signup(email, password) {
    return auth.createUserWithEmailAndPassword(email, password);
  }

  function login(email, password) {
    return auth.signInWithEmailAndPassword(email, password);
  }

  function logout() {
    return auth.signOut();
  }

  function resetPassword(email) {
    return auth.sendPasswordResetEmail(email);
  }

  function updateEmail(email) {
    return currentUser.updateEmail(email);
  }

  function updatePassword(password) {
    return currentUser.updatePassword(password);
  }

  function reauthenticate(currentPassword) {
    const cred = firebase.auth.EmailAuthProvider.credential(
      currentUser.email,
      currentPassword
    );
    return currentUser.reauthenticateWithCredential(cred);
  }

  useEffect(() => {
    const unsubscribe = auth.onIdTokenChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        const idToken = await user.getIdToken();
        setToken(idToken);
      } else {
        setToken(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Fetch user role from Firebase when auth user changes
  const refreshUserData = async () => {
    const currentAuthUser = auth.currentUser;
    if (!currentAuthUser) return;
    
    // Only show loading if we don't already have cached details for this user.
    if (!userDetails || userDetails.userId !== currentAuthUser.uid) {
      setRoleLoading(true);
    }
    try {
      const details = await getUserByID(currentAuthUser.uid);
      const detailsWithId = details
        ? { userId: currentAuthUser.uid, ...details }
        : null;
      setUserDetails(detailsWithId);
      setUserRole(detailsWithId?.role || null);
      writeCachedUserDetails(detailsWithId);
    } catch (err) {
      console.error("Error fetching user role:", err);
    } finally {
      setRoleLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      refreshUserData();
    } else if (currentUser === null) {
      // Explicitly signed out (not just initial undefined state).
      setUserDetails(null);
      setUserRole(null);
      setRoleLoading(false);
      writeCachedUserDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  const value = {
    currentUser,
    userDetails,
    userRole,
    roleLoading,
    login,
    signup,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    reauthenticate,
    refreshUserData,
    isSigningUp,
    setIsSigningUp,
    token,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
